"use client";

import { useAuiState } from "@assistant-ui/react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRouteContext } from "./contexts/routeContext";
import { useThreadContext } from "./contexts/threadContext";
import { RemoteThreadDetails } from "./providers/customThreadListProvider";
import { apiAuthFetch } from "@/lib/authFetch";
import { appendLocParam } from "@/lib/i18n";

const fetchThread = async (id: string): Promise<RemoteThreadDetails> => {
  const response = await apiAuthFetch(appendLocParam(`api/threads/${id}`));
  const thread: RemoteThreadDetails = await response.json();
  return thread;
};

export default function RouteWatcher() {
  const threadId = useAuiState((state) => state.threadListItem.id);
  // After initialize(), AUI sets .remoteId on the list item but keeps .id as __LOCAL_xxx.
  // Use remoteId when available so effects fire as soon as the thread is persisted.
  const remoteThreadId = useAuiState((state) => state.threadListItem.remoteId);
  const effectiveId = remoteThreadId ?? threadId;

  const prevEffectiveId = useRef<string | undefined>(undefined);
  const routeContext = useRouteContext();
  const threadContext = useThreadContext();
  const navigate = useNavigate();

  // Refs so the effect can stay keyed only on `effectiveId` while still
  // reading the latest contexts at call time. Without these, the effect
  // captures routeContext/threadContext values from the render that fired
  // it, and a thread switch that lands shortly after a routeContext
  // mutation would push the URL using stale caseId / chatsView /
  // settingsView. The previous shape mixed direct reads with refs, which
  // is the inconsistency that produced the "opens the wrong thread" tail.
  const routeContextRef = useRef(routeContext);
  useEffect(() => {
    routeContextRef.current = routeContext;
  }, [routeContext]);
  const threadContextRef = useRef(threadContext);
  useEffect(() => {
    threadContextRef.current = threadContext;
  }, [threadContext]);

  // AUI → Route: when effectiveId changes, update routeContext and resolve selectedDocs.
  // The Route→AUI direction is handled by passing routeContext.threadId to
  // useRemoteThreadListRuntime({ threadId }) in CustomThreadListProvider.
  useEffect(() => {
    const prev = prevEffectiveId.current;
    prevEffectiveId.current = effectiveId;

    if (prev === undefined) return; // first render, no switch yet
    if (prev === effectiveId) return;

    const ctx = routeContextRef.current;
    const tctx = threadContextRef.current;
    const onChatsRoute = ctx.chatsView === true;
    const onSettingsRoute = ctx.settingsView != null;

    // On /settings the URL is owned by the settings route. A sidebar thread
    // click or new-thread is a navigation intent — push to the thread URL.
    if (onSettingsRoute) {
      if (!effectiveId || effectiveId.startsWith("__LOCAL")) {
        const dest = ctx.launchCaseId ?? "default";
        navigate(appendLocParam(`/${dest}`));
        return;
      }
      const cached = tctx.threadItems.find(
        (item) => item.remoteId === effectiveId,
      );
      const proj = cached?.caseId ?? ctx.caseId ?? "default";
      navigate(appendLocParam(`/${proj}/${effectiveId}`));
      return;
    }

    // On /chats the URL is owned by the route itself. A sidebar thread
    // click or "New chat" is a real navigation intent — push to the
    // destination URL via react-router's navigate() so the route state
    // stays in sync with the URL bar (raw window.history.pushState desync
    // was the root cause of "click does nothing" follow-up navigations).
    if (onChatsRoute) {
      if (!effectiveId || effectiveId.startsWith("__LOCAL")) {
        const dest = ctx.launchCaseId ?? "default";
        navigate(appendLocParam(`/${dest}`));
        return;
      }
      const cached = tctx.threadItems.find(
        (item) => item.remoteId === effectiveId,
      );
      const proj = cached?.caseId ?? ctx.caseId ?? "default";
      navigate(appendLocParam(`/${proj}/${effectiveId}`));
      return;
    }

    if (!effectiveId || effectiveId.startsWith("__LOCAL")) {
      // New thread: keep launch case context, start with 0 docs.
      tctx.onThreadStateChanged({ selectedDocs: [] });
      ctx.onRouteStateChanged({
        threadId: undefined,
        caseId: ctx.launchCaseId,
        docIds: [],
        lastThreadId: ctx.threadId,
        loadingThreadFromUrl: false,
      });
      return;
    }

    // loadingThreadFromUrl is owned end-to-end: set here on a real thread switch
    // (routeContextWrapper can't detect it — by the time the URL updates we've
    // already mutated prev.threadId), cleared by threadHistoryProvider.load() on
    // success or failure.
    ctx.onRouteStateChanged({
      threadId: effectiveId,
      lastThreadId: ctx.threadId,
      docIds: [],
      loadingThreadFromUrl: true,
    });

    // Resolve selectedDocs: use cache when available, fall back to a fetch
    const cached = tctx.threadItems.find(
      (item) => item.remoteId === effectiveId,
    );
    if (cached) {
      tctx.onThreadStateChanged({ selectedDocs: cached.selectedDocs });
    } else {
      const fetchId = effectiveId;
      fetchThread(effectiveId).then((details) => {
        if (prevEffectiveId.current !== fetchId) return;
        threadContextRef.current.onThreadStateChanged({
          selectedDocs: details.selectedDocs ?? [],
        });
      });
    }
  }, [effectiveId, navigate]);

  // URL bar mirror: keep the address bar in sync with routeContext using
  // window.history.replaceState. This is intentionally NOT navigate() —
  // we do NOT want a full route transition every time AUI emits a new
  // remoteId mid-flight (it would unmount the active route component,
  // destroy the in-flight runtime, and trigger the loading state). The
  // state change has already happened in React; we just want the URL bar
  // to catch up. Cross-route NAVIGATION goes through navigate() above.
  useEffect(() => {
    if (routeContext.chatsView) return;
    if (routeContext.settingsView != null) return;
    const isNew = !effectiveId || effectiveId.startsWith("__LOCAL");

    let desired = "";

    desired += routeContext.caseId
      ? `/${routeContext.caseId}`
      : "/default";

    if (effectiveId && !isNew) {
      desired += `/${effectiveId}`;
    }

    if (isNew && routeContext.docIds && routeContext.docIds.length > 0) {
      const docIdsPart = `${getSeparator(desired)}docIds=${routeContext.docIds.join(",")}`;
      desired += docIdsPart;
    }

    if (routeContext.showThreadList === false) {
      const showThreadListPart = `${getSeparator(desired)}showThreadList=false`;
      desired += showThreadListPart;
    } else {
      const showThreadListPart = `${getSeparator(desired)}showThreadList=true`;
      desired += showThreadListPart;
    }

    // Always append the locale param via the shared utility so every
    // URL rewrite carries `loc=X` (including the default `cs`).
    desired = appendLocParam(desired);

    const encoded = encodeURI(desired);
    if (typeof window !== "undefined") {
      const currentUrl = window.location.pathname + window.location.search;
      if (currentUrl === encoded) return;
      window.history.replaceState(null, "", encoded);
    }
  }, [
    routeContext.caseId,
    routeContext.docIds,
    routeContext.showThreadList,
    routeContext.chatsView,
    routeContext.settingsView,
    effectiveId,
  ]);

  return null;
}

function getSeparator(routeUrl: string) {
  return routeUrl.includes("?") ? "&" : "?";
}
